import { createBillItem } from "../../api/bills";
import "../UI/AddButton/add.css";
import { Col, Row, Modal, Form, Button, Input, Select, DatePicker, Switch, message } from "antd";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import Title from "../UI/Title/Title";
import { useMutation } from "react-query";
import SelectDiscount from "../UI/DiscountType/discounttype";
import { getItemsDropDownSearch } from "../../api/itemapi";
import debounce from 'lodash/debounce';
import { Pagination } from "./Types";

const { TextArea } = Input;

type propsType = {
  openScan: boolean;
  setOpenScan: React.Dispatch<React.SetStateAction<boolean>>;
  billId: string | undefined;
  handleTableChange: React.Dispatch<any>;
  pagination: Pagination;
};

interface options {
  value: number;
  label: string;
}

export const ScanToAdd = ({ openScan, setOpenScan, billId, handleTableChange, pagination,}: propsType) => {
  const [t, i18n] = useTranslation("common");
  const isCurrentLangArabic = i18n.language === 'ar'
  const [items, setItems] = useState<options[] | undefined>(undefined); // Initialize items state
  const [searchQuery, setSearchQuery] = useState('');
  const [form] = Form.useForm();
  const [isDiscount, setIsDiscount] = useState<boolean>(false)
  const [isTaxable, setIsTaxable] = useState<boolean>(false)
  const [isBtnAddClicked, setisBtnAddClicked] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { mutateAsync } = useMutation(getItemsDropDownSearch); // Use the correct API function

  const handleSearch = debounce(async () => {
    if (searchQuery.trim()) {
      try {
        setIsLoading(true);
        const response = await mutateAsync(searchQuery);
        const data = response.data.map((item: any) => ({
          value: item.id,
          label: item.name
        }));

        if (data.length === 0) {
          message.warning("No items found");
          form.resetFields(['itemId']);
        } else {
          form.setFieldsValue({ itemId: data[0].value });
        }

        setItems(data);
        setSearchQuery(''); // Clear the search text
        setIsLoading(false);

        form.resetFields(['search']);
      } catch (error) {
        console.error('Error fetching data:', error);
        setIsLoading(false);
      }
    }
  }, 300);

  const handleKeyDown = (e: any) => {
    if (e.key === 'F9') {
      handleSaveAndAdd()
    } else if (e.key === 'F10') {
      handleSaveAndClose()
    } else if (e.key === 'Enter') {
      handleSearch()
    }
  }
  

  const { mutate: createBillItemMutation, isLoading: isLoadingCreateBillItemScanMutation } = useMutation("createBillItem",
    (billItem: any) => createBillItem(billId, billItem),
    {
      onSuccess: ({ data }: any) => {
        message.success(data.message)
        form.resetFields();
        setOpenScan(false);
        if (isBtnAddClicked) {
          setTimeout(() => {
            setisBtnAddClicked(false);
            setOpenScan(true);
          }, 400);
        }
      },
      onError: ({ response }: any) => {
        message.error(response.data.message)
      },
    }
  );

  const handleDiscountSwitch = (checked: any) => {
    setIsDiscount(checked);
  };

  const handleTaxableSwitch = (checked: any) => {
    setIsTaxable(checked);
  };

  const handleCancel = () => {
    setisBtnAddClicked(false)
    setIsTaxable(false)
    setIsDiscount(false)
    form.resetFields();
    setOpenScan(false);
  }

  const handleSaveAndAdd = () => {
    setisBtnAddClicked(true)
    handleSubmit();
  };

  const handleSaveAndClose = () => {
    setisBtnAddClicked(false)
    handleSubmit();
  }

  const handleSubmit = () => {
    form.submit();
    handleTableChange(pagination);
  };

  function onFinish(values: any) {
    createBillItemMutation(values)
  }

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  });

  return (
    <Modal
      closable={false}
      maskClosable={false}
      width={950}
      centered
      onCancel={handleCancel}
      open={openScan}
      footer={null}
      destroyOnClose
    >
      <Form layout="vertical" form={form} onFinish={onFinish}>
        <Row>
          <Col offset={1}>
            <Title title={`${t("add-items-to-bills")}`} />
          </Col>
          <Col style={{ fontWeight: 'bolder', ...(isCurrentLangArabic ? { marginRight: '450px' } : { marginLeft: '520px' }) }}>
            <table>
              <tbody>
                <tr>
                  <td style={isCurrentLangArabic ? { textAlign: 'left', paddingLeft: '5px' } : { textAlign: 'right', paddingRight: '5px' }}>[F9]:</td>
                  <td>{t('save-&-add')}</td>
                </tr>
                <tr>
                  <td style={isCurrentLangArabic ? { textAlign: 'left', paddingLeft: '5px' } : { textAlign: 'right', paddingRight: '5px' }}>[F10]:</td>
                  <td>{t('save-&-close')}</td>
                </tr>
              </tbody>
            </table>
          </Col>
        </Row>
        <Row gutter={[0, 10]}>
          <Col offset={1}
            span={8}
            style={{ marginTop: "18px" }}
          >
            <Form.Item
              name="search"
              label={`${t("search")}`}
              rules={[
                {
                  required: false
                },
              ]}
            >
              <Input
                placeholder={`${t("enter-text")}`}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown} // Add the event listener
              />
            </Form.Item>

            <Form.Item
              name="itemId"
              label={`${t("item")}`}
              rules={[
                {
                  required: true,
                  message: "item is requried",
                },
              ]}
            >
              <Select
                showSearch={true}
                optionFilterProp="label"
                placeholder={`${t("select-item")}`}
                options={items}
                loading={isLoading}
              />
            </Form.Item>

            <Form.Item name="quantity"
              label={`${t("quantity")}`}
              rules={[
                {
                  required: true,
                },
              ]}>
              <Input
                autoComplete="off"
                placeholder={`${t("enter-text")}`}
                data-testid="price"
              />
            </Form.Item>

            <Form.Item
              name="price"
              label={`${t("purchase-price")}`}
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input
                autoComplete="off"
                placeholder={`${t("enter-text")}`}
                data-testid="price"
              />
            </Form.Item>
            <Form.Item name="sales" label={`${t("sales-price")}`}
              rules={[
                {
                  required: true,
                },
              ]}>
              <Input
                placeholder={`${t("enter-text")}`}
                data-testid="sales"
                autoComplete="off"
              />
            </Form.Item>
          </Col>
          <Col offset={2} span={9}
            style={{ marginTop: "18px" }}
          >
            <Form.Item name="date" label={`${t("expiration-date")}`}>
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
            
            <Form.Item name="taxable" label={`${t("tax")}`} rules={[{ required: isTaxable }]}>
              <div style={{ display: 'flex', alignItems: 'center', gap: "20px" }}>
                <Switch checkedChildren={`${t("yes")}`} unCheckedChildren={`${t("no")}`} onChange={handleTaxableSwitch} style={{ width: '80px', height: "25px" }} />
                <Input autoComplete="off" placeholder={`${t("enter-text")}`} maxLength={10} data-testid="taxable" disabled={!isTaxable}/>
              </div>
            </Form.Item>

            <Form.Item name="isDiscountable" label={`${t("discount")}`} rules={[{ required: isDiscount }]}>
              <div  style={{ display: 'flex', alignItems: 'center', gap: "20px" }}>
                <div>
                  <Switch checkedChildren={`${t("yes")}`} unCheckedChildren={`${t("no")}`} onChange={handleDiscountSwitch} style={{ width: '4rem', height: "25px" }} />
                </div>
                  
                <div  style={{ display: 'flex', alignItems: 'center', gap: "20px", width:"100%" }}>
                  <SelectDiscount isDiscountable = {isDiscount}/>
                  <Input maxLength={10} disabled={!isDiscount}  autoComplete="off" placeholder={`${t("enter-text")}`}/>
                </div>                
              </div>
            </Form.Item>

            <Form.Item name="notes" label={`${t("notes")}`}>
              <TextArea
                autoComplete="off"
                className="scrollable-textarea"
                data-testid="description-input"
                placeholder={`${t("enter-text")}`}
                autoSize={{ minRows: 4, maxRows: 4 }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row style={{ marginTop: "20px" }}>
          <Col
            offset={1}
          >
            <Button
              size="large"
              style={{
                color: "#1677FF",
                border: "1px solid #1677FF",
                width: "110px",
              }}
              onClick={handleCancel}
            >
              {t("cancel")}
            </Button>
          </Col>
          <Col offset={12}>
            <Button
              onClick={handleSaveAndAdd}
              style={{
                width: "110px",
                background: "#1677FF",
                color: "#fff",
                border: "none",
              }}
              size="large"
            >
              {t("save-&-add")}
            </Button>
          </Col>
          <Col offset={1}>
            {" "}
            <Button
              style={{
                background: "#1677FF",
                color: "#fff",
                border: "none",
              }}
              size="large"
              onClick={handleSaveAndClose}
            >
              {t("save-&-close")}
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
